import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CameraIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { ocrService } from '../services/ocrService';
import { calculateTaxes, formatCurrency, parseCurrency } from '../utils/taxCalculations';
import { validatePoliceField as validateField, validatePoliceForm as validateForm, isMinor } from '../utils/policeFormValidation';
import { FormInput } from '../components/checkin/FormInput';
import { FormSelect } from '../components/checkin/FormSelect';
import { SearchableSelect } from '../components/checkin/SearchableSelect';
import { WebcamCapture } from '../components/checkin/WebcamCapture';
import { SignaturePad } from '../components/checkin/SignaturePad';
import { useOrientationPersistence, useLoadPersistedData } from '../hooks/useOrientationPersistence';
import {
  ISO_NATIONALITIES,
  STAY_PURPOSES,
  DOCUMENT_TYPES,
} from '../utils/referenceData';
import { listRoomTypes, listRoomRates, listAvailableRooms } from '../services/roomService';
import type { RoomType as RoomTypeModel, RoomRate } from '../types/rooms';
import type {
  EntryMode,
  CheckInFormData,
  OCRResult,
  FormErrors,
  TaxBreakdown,
  CheckInRequest,
  DocumentType,
  Gender,
  RoomType,
  PaymentType,
  AmountMode,
} from '../types/checkin';

const FORM_DATA_KEY = 'checkin-form-data';

function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('Impossible de lire l\'image du document'));
    };
    reader.onerror = () => reject(reader.error || new Error('Impossible de lire l\'image du document'));
    reader.readAsDataURL(file);
  });
}

const CheckInPage: React.FC = () => {
  const navigate = useNavigate();

  // Load persisted data on mount (for orientation changes)
  const initialFormData: CheckInFormData = useLoadPersistedData(FORM_DATA_KEY, {
    fullName: '',
    gender: '',
    dateOfBirth: '',
    nationality: '',
    documentType: '',
    documentNumber: '',
    documentExpiryDate: '',
    profession: '',
    email: '',
    phoneNumber: '',
    facePhotoBase64: '',
    signatureBase64: '',
    documentPhotoUrl: '',
    roomType: '',
    roomNumber: '',
    checkInDate: new Date().toISOString().split('T')[0],
    checkOutDate: '',
    bookingReference: '',
    provenance: '',
    stayPurpose: '',
    paymentType: '',
    amountMode: 'HT' as AmountMode,
    amount: '',
    isTaxExempt: false,
    exemptionType: '',
    exemptionReference: '',
    exemptionReason: '',
    exemptionValidFrom: '',
    exemptionValidUntil: '',
    exemptionCertificate: '',
  });

  // Mode state
  const [mode, setMode] = useState<EntryMode>('ocr');
  const [ocrResult, setOcrResult] = useState<OCRResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ocrError, setOcrError] = useState<string>('');
  const [showDocumentWebcam, setShowDocumentWebcam] = useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);

  // Form state - Initialize with persisted data
  const [formData, setFormData] = useState<CheckInFormData>(initialFormData);

  const [errors, setErrors] = useState<FormErrors>({});
  const [taxBreakdown, setTaxBreakdown] = useState<TaxBreakdown | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showFaceWebcam, setShowFaceWebcam] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);

  // Dynamic room types and rates from Administration module
  const [dynamicRoomTypes, setDynamicRoomTypes] = useState<{ value: string; label: string; id?: string }[]>([]);
  const [roomRates, setRoomRates] = useState<RoomRate[]>([]);
  const [allAvailableRooms, setAllAvailableRooms] = useState<any[]>([]);
  const [availableRooms, setAvailableRooms] = useState<{ value: string; label: string }[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);

  // Map room type name → ID for rate matching
  const roomTypeNameToIdRef = useRef<Record<string, string>>({});

  // Track whether amount was auto-populated (vs user-entered)
  const amountAutoPopulatedRef = useRef(false);

  // Track matched rate currency (from room_rates)
  const [rateCurrency, setRateCurrency] = useState<string>('');

  // Fetch room types and rates from API on mount
  useEffect(() => {
    const loadRoomData = async () => {
      try {
        const [types, rates] = await Promise.all([
          listRoomTypes(),
          listRoomRates({ active: true }),
        ]);
        if (types.length > 0) {
          setDynamicRoomTypes(types.map((rt) => ({ value: rt.name, label: rt.name, id: rt.id })));
          // Build name→id lookup for rate matching
          const nameToId: Record<string, string> = {};
          types.forEach((rt) => { nameToId[rt.name.toLowerCase()] = rt.id; });
          roomTypeNameToIdRef.current = nameToId;
        }
        setRoomRates(rates);
      } catch (err) {
        console.warn('Impossible de charger les types de chambre:', err);
      }
    };
    loadRoomData();
  }, []);

  // Fetch available rooms when dates change
  useEffect(() => {
    if (!formData.checkInDate) return;
    // Reject incomplete/corrupt dates (e.g. year 0202 from partial inputs)
    const isoDate = /^\d{4}-\d{2}-\d{2}$/;
    if (!isoDate.test(formData.checkInDate)) {
      setAllAvailableRooms([]);
      return;
    }
    const checkOut = formData.checkOutDate || (() => {
      const d = new Date(`${formData.checkInDate}T00:00:00`);
      if (Number.isNaN(d.getTime())) return '';
      d.setDate(d.getDate() + 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    })();
    if (!checkOut || !isoDate.test(checkOut) || checkOut <= formData.checkInDate) {
      setAllAvailableRooms([]);
      return;
    }
    setLoadingRooms(true);
    listAvailableRooms(formData.checkInDate, checkOut)
      .then((rooms) => {
        setAllAvailableRooms(rooms);
      })
      .catch(() => setAllAvailableRooms([]))
      .finally(() => setLoadingRooms(false));
  }, [formData.checkInDate, formData.checkOutDate]);

  // Filter available rooms by selected room type
  useEffect(() => {
    let filtered = allAvailableRooms;
    if (formData.roomType) {
      const typeMatch = filtered.filter((r: any) =>
        (r.room_type_name || '').toLowerCase() === formData.roomType.toLowerCase()
      );
      if (typeMatch.length > 0) filtered = typeMatch;
    }
    setAvailableRooms(
      filtered.map((r: any) => ({
        value: r.room_number,
        label: `${r.room_number}${r.room_type_name ? ` — ${r.room_type_name}` : ''}${r.floor ? ` (Étage ${r.floor})` : ''}`,
      }))
    );
  }, [allAvailableRooms, formData.roomType]);

  // Auto-populate rate when room type changes
  useEffect(() => {
    if (!formData.roomType || roomRates.length === 0) return;
    // Look up room type ID from name, then match rate by room_type_id
    const roomTypeId = roomTypeNameToIdRef.current[formData.roomType.toLowerCase()];
    const checkInDate = formData.checkInDate || new Date().toISOString().slice(0, 10);
    const matchingRates = roomRates.filter((r) => {
      const typeMatches = r.room_type_id === roomTypeId ||
        (r.room_type_name || '').toLowerCase() === formData.roomType.toLowerCase();
      return r.is_active && typeMatches && r.start_date <= checkInDate && r.end_date >= checkInDate;
    });
    const matchingRate = [...matchingRates].sort((a, b) => {
      const aUpdated = new Date(a.updated_at || a.created_at).getTime();
      const bUpdated = new Date(b.updated_at || b.created_at).getTime();
      return bUpdated - aUpdated;
    })[0];
    if (matchingRate) {
      const rateAmountMode: AmountMode = matchingRate.amount_type === 'TTC' ? 'TTC' : 'HT';
      setRateCurrency(matchingRate.currency || 'USD');
      setFormData((prev) => {
        // Only auto-populate if the amount is empty or was previously auto-populated
        if (prev.amount && !amountAutoPopulatedRef.current) return prev;
        amountAutoPopulatedRef.current = true;
        return {
          ...prev,
          amount: String(matchingRate.price_per_night),
          amountMode: rateAmountMode,
        };
      });
    } else {
      // No matching rate — reset to default HT
      setRateCurrency('');
      if (amountAutoPopulatedRef.current) {
        amountAutoPopulatedRef.current = false;
        setFormData((prev) => ({ ...prev, amount: '', amountMode: 'HT' as AmountMode }));
      }
    }
  }, [formData.checkInDate, formData.roomType, roomRates]);

  // Persist form data on orientation change
  useOrientationPersistence(FORM_DATA_KEY, formData, true);

  // Calculate taxes when amount or mode changes
  useEffect(() => {
    if (formData.amount) {
      const amount = parseCurrency(formData.amount);
      if (amount > 0) {
        const breakdown = calculateTaxes(amount, formData.amountMode);
        setTaxBreakdown(breakdown);
      } else {
        setTaxBreakdown(null);
      }
    } else {
      setTaxBreakdown(null);
    }
  }, [formData.amount, formData.amountMode]);

  // Process a document file (from upload, paste, or drop)
  const processDocumentFile = useCallback(async (file: File) => {
    if (!file || !file.type.startsWith('image/')) return;

    setIsProcessing(true);
    setOcrError('');
    setOcrResult(null);

    try {
      // Always preserve the uploaded document image for registration,
      // even if OCR later falls back to manual entry.
      const documentPhotoUrl = await readFileAsDataURL(file);
      setFormData((prev) => ({
        ...prev,
        documentPhotoUrl,
      }));

      // Process document with OCR
      const result = await ocrService.processAuto(file);

      setOcrResult(result);

      // Always pre-fill form with whatever OCR found
      setFormData((prev) => ({
        ...prev,
        fullName: result.extractedData.fullName || prev.fullName,
        gender: result.extractedData.gender || prev.gender,
        dateOfBirth: result.extractedData.dateOfBirth || prev.dateOfBirth,
        nationality: result.extractedData.nationality || prev.nationality,
        documentType: result.extractedData.documentType || prev.documentType,
        documentNumber: result.extractedData.documentNumber || prev.documentNumber,
        documentExpiryDate: result.extractedData.documentExpiryDate || prev.documentExpiryDate,
        profession: result.extractedData.profession || prev.profession,
        facePhotoBase64: result.extractedData.facePhotoBase64 || prev.facePhotoBase64,
      }));

      // If confidence is low, switch to manual mode but keep pre-filled data
      if (result.confidence < 70) {
        setOcrError(`Confiance faible (${result.confidence}%). Vérifiez et complétez les champs.`);
        setMode('manual');
      }
    } catch (error: any) {
      setOcrResult(null);
      const status = error?.response?.status;
      let msg = error?.response?.data?.error || error?.response?.data?.message || error?.message;
      if (typeof msg === 'object' && msg?.message) msg = msg.message;
      if (status === 422) {
        msg = 'Document illisible ou non reconnu. Saisissez les informations manuellement.';
      } else if (status === 503 || status === 504 || status === 502) {
        msg = 'Service OCR temporairement indisponible. Saisissez les informations manuellement.';
      } else if (!msg || msg === 'Network Error') {
        msg = 'Échec du traitement du document. Saisissez les informations manuellement.';
      }
      setOcrError(String(msg));
      setMode('manual');
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Wrapper for file input change events
  const handleDocumentUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) processDocumentFile(file);
  }, [processDocumentFile]);

  // Handle paste from clipboard (Ctrl+V / Cmd+V with screenshot)
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (mode !== 'ocr' || isProcessing) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) processDocumentFile(file);
          break;
        }
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [mode, isProcessing, processDocumentFile]);

  // Webcam: start camera
  const startWebcam = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      streamRef.current = stream;
      setShowDocumentWebcam(true);
    } catch {
      setOcrError('Impossible d\'accéder à la caméra. Vérifiez les permissions.');
    }
  }, []);

  // Attach stream to video element once it's rendered
  useEffect(() => {
    if (showDocumentWebcam && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [showDocumentWebcam]);

  // Webcam: capture frame and process
  const captureWebcam = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'webcam-capture.jpg', { type: 'image/jpeg' });
        stopWebcam();
        processDocumentFile(file);
      }
    }, 'image/jpeg', 0.92);
  }, [processDocumentFile]);

  // Webcam: stop camera
  const stopWebcam = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setShowDocumentWebcam(false);
  }, []);

  // Clean up webcam on unmount
  useEffect(() => { return () => { streamRef.current?.getTracks().forEach(t => t.stop()); }; }, []);

  // Handle mode switch
  const handleModeSwitch = useCallback(() => {
    setMode((prev) => (prev === 'ocr' ? 'manual' : 'ocr'));
    setOcrError('');
  }, []);

  // Handle field change with validation
  const handleFieldChange = useCallback((name: keyof CheckInFormData, value: any) => {
    // Mark amount as user-entered when user types in the amount field
    if (name === 'amount') {
      amountAutoPopulatedRef.current = false;
    }

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      // Real-time validation using the updated state
      const error = validateField(name, value, updated);
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: error,
      }));
      return updated;
    });
  }, []);

  // Handle amount mode switch
  const handleAmountModeSwitch = useCallback(() => {
    setFormData((prev) => {
      const newMode: AmountMode = prev.amountMode === 'HT' ? 'TTC' : 'HT';
      
      // Preserve total amount when switching modes
      if (taxBreakdown) {
        const newAmount = newMode === 'HT' ? taxBreakdown.amountHT : taxBreakdown.amountTTC;
        return {
          ...prev,
          amountMode: newMode,
          amount: formatCurrency(newAmount),
        };
      }
      
      return {
        ...prev,
        amountMode: newMode,
      };
    });
  }, [taxBreakdown]);

  // Handle webcam capture
  const handleWebcamCapture = useCallback((photoBase64: string) => {
    setFormData((prev) => ({
      ...prev,
      facePhotoBase64: photoBase64,
    }));
    setShowFaceWebcam(false);
  }, []);

  // Handle signature capture
  const handleSignatureCapture = useCallback((signatureBase64: string) => {
    setFormData((prev) => ({
      ...prev,
      signatureBase64: signatureBase64,
    }));
  }, []);

  // Map frontend document type to backend format
  const mapDocumentType = (docType: string): CheckInRequest['client']['document_type'] => {
    const mapping: Record<string, string> = {
      'Passport': 'passport',
      'National ID Card (CNI)': 'national_id',
      'Driving License': 'driver_license',
      'Visa': 'passport', // Map visa to passport for backend
    };
    return (mapping[docType] || 'passport') as CheckInRequest['client']['document_type'];
  };

  // Map frontend gender to backend format
  const mapGender = (gender: string): CheckInRequest['client']['gender'] => {
    const mapping: Record<string, string> = {
      'Male': 'male',
      'Female': 'female',
      'Other': 'other',
    };
    return (mapping[gender] || 'other') as CheckInRequest['client']['gender'];
  };

  // Map frontend payment type to backend format
  const mapPaymentType = (paymentType: string): NonNullable<CheckInRequest['payment']['payment_mode']> => {
    const mapping: Record<string, string> = {
      'Cash': 'cash',
      'USD Cash': 'usd_cash',
      'Credit Card (CB)': 'card',
      'Bank Transfer': 'bank_transfer',
      'Mobile Money': 'mobile',
      'Other': 'cash',
    };
    return mapping[paymentType] || 'cash';
  };

  // Handle form submission
  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();

    // Validate form
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // Scroll to first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      const el = document.getElementById(firstErrorField) || document.querySelector(`[name="${firstErrorField}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!taxBreakdown) {
      setErrors({ amount: 'Veuillez saisir un montant valide' });
      return;
    }

    // Check authentication before submitting
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No authentication token found');
      setErrors({ submit: 'Session expirée. Veuillez vous reconnecter.' });
      setTimeout(() => navigate('/login'), 2000);
      return;
    }
    // Token verified

    setIsSubmitting(true);

    try {
      // Prepare check-in request in backend expected format
      // Backend expects: client, stay, payment (not guest, accommodation, payment)
      const clientData: CheckInRequest['client'] = {
        name: formData.fullName,
        gender: mapGender(formData.gender),
        date_of_birth: formData.dateOfBirth,
        nationality: formData.nationality,
        document_type: mapDocumentType(formData.documentType),
        document_number: formData.documentNumber,
      };
      
      // Add optional client fields only if they have values
      if (formData.documentExpiryDate) clientData.document_expiry = formData.documentExpiryDate;
      if (formData.profession) clientData.occupation = formData.profession;
      if (formData.email) clientData.email = formData.email;
      if (formData.phoneNumber) clientData.phone_number = formData.phoneNumber;
      if (formData.facePhotoBase64) clientData.photo = formData.facePhotoBase64;
      if (formData.signatureBase64) clientData.signature = formData.signatureBase64;
      if (formData.documentPhotoUrl) clientData.document_photo = formData.documentPhotoUrl;

      const stayData: CheckInRequest['stay'] = {
        room_number: formData.roomNumber,
        arrival_date: formData.checkInDate,
      };
      
      // Add optional stay fields only if they have values
    if (formData.roomType) {
      stayData.room_type = formData.roomType;
      const roomTypeID = roomTypeNameToIdRef.current[formData.roomType.toLowerCase()];
      if (roomTypeID) stayData.room_type_id = roomTypeID;
      if (rateCurrency) stayData.currency = rateCurrency;
    }
      if (formData.checkOutDate) stayData.departure_date = formData.checkOutDate;
      if (formData.bookingReference) stayData.booking_reference = formData.bookingReference;
      if (formData.provenance) stayData.provenance = formData.provenance;
      if (formData.stayPurpose) stayData.stay_purpose = formData.stayPurpose;

      // Calculate rate_per_night from accommodation amount and number of nights
      // This ensures the backend always receives the nightly rate for FPT calculation
      if (taxBreakdown && taxBreakdown.amountHT > 0) {
        let nights = 1;
        if (formData.checkInDate && formData.checkOutDate) {
          const diffMs = new Date(formData.checkOutDate).getTime() - new Date(formData.checkInDate).getTime();
          const diffNights = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
          if (diffNights > 0) nights = diffNights;
        }
        stayData.rate_per_night = Math.round((taxBreakdown.amountHT / nights) * 100) / 100;
      }

      const backendRequest: CheckInRequest = {
        client: clientData,
        stay: stayData,
        payment: {
          accommodation_amount: taxBreakdown.amountHT,
          payment_mode: mapPaymentType(formData.paymentType),
          is_tax_exempt: formData.isTaxExempt,
          exemption_type: formData.exemptionType || undefined,
          exemption_reference: formData.exemptionReference || undefined,
          exemption_reason: formData.exemptionReason || undefined,
          exemption_valid_from: formData.exemptionValidFrom || undefined,
          exemption_valid_until: formData.exemptionValidUntil || undefined,
          exemption_certificate: formData.exemptionCertificate || undefined,
        },
      };

      // Submit to API
      const { guestService } = await import('../services/guestService');
      await guestService.checkIn(backendRequest);

      // Clear persisted form data on success
      try { localStorage.removeItem(FORM_DATA_KEY); } catch {}

      // Show success message, then navigate to guest list
      setSuccessMessage('Fiche de police enregistree avec succes !');
      setTimeout(() => navigate('/guests'), 1500);
    } catch (error: any) {
      // Log detailed error for debugging
      console.error('Check-in error:', error);
      if (error.response?.status === 401) {
        setErrors({ submit: 'Session expirée. Veuillez vous reconnecter.' });
        setTimeout(() => navigate('/login'), 2000);
      } else {
        const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message || 'Échec de l\'enregistrement du client';
        setErrors({ submit: errorMessage });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, taxBreakdown, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Header - Responsive text sizes */}
        <div className="mb-4 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Fiche de Police — Enregistrement Client</h1>
          <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600">
            Scannez le document du client ou saisissez les informations manuellement
          </p>
        </div>

        {/* Step Indicator */}
        <div className="mb-4 sm:mb-6 bg-white rounded-lg shadow p-3 sm:p-4">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Identite' },
              { num: 2, label: 'Document' },
              { num: 3, label: 'Sejour' },
              { num: 4, label: 'Paiement' },
              { num: 5, label: 'Photo & Signature' },
            ].map((step, idx) => (
              <div key={step.num} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs sm:text-sm font-semibold">
                    {step.num}
                  </div>
                  <span className="mt-1 text-[10px] sm:text-xs text-gray-600 text-center hidden sm:block">{step.label}</span>
                </div>
                {idx < 4 && (
                  <div className="flex-1 h-0.5 bg-purple-200 mx-1 sm:mx-2" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Mode Indicator - Stack on mobile, horizontal on tablet+ */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white rounded-lg shadow p-3 sm:p-4 space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-2 sm:space-x-3">
            {mode === 'ocr' ? (
              <>
                <CameraIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 flex-shrink-0" />
                <span className="text-base sm:text-lg font-medium text-gray-900">Mode Scan OCR</span>
              </>
            ) : (
              <>
                <DocumentTextIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600 flex-shrink-0" />
                <span className="text-base sm:text-lg font-medium text-gray-900">Mode Saisie Manuelle</span>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={handleModeSwitch}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 sm:py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-h-touch"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Passer en {mode === 'ocr' ? 'saisie manuelle' : 'scan OCR'}
          </button>
        </div>

        {/* OCR Upload Section - Responsive padding and spacing */}
        {mode === 'ocr' && (
          <div className="mb-4 sm:mb-6 bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">
              Scanner le Document
              <span className="text-red-500 ml-1">*</span>
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mb-4">
              Le scan du document d'identité avec lecture OCR est obligatoire
            </p>
            
            <div
              className={`border-2 border-dashed rounded-lg p-6 sm:p-8 text-center transition-colors ${
                errors.documentPhotoUrl ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/30'
              }`}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-blue-500', 'bg-blue-50'); }}
              onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50'); }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                const file = e.dataTransfer.files?.[0];
                if (file && file.type.startsWith('image/')) {
                  const input = document.getElementById('document-upload') as HTMLInputElement;
                  const dt = new DataTransfer();
                  dt.items.add(file);
                  input.files = dt.files;
                  input.dispatchEvent(new Event('change', { bubbles: true }));
                }
              }}
            >
              {formData.documentPhotoUrl ? (
                <div className="relative inline-block">
                  <img
                    src={formData.documentPhotoUrl}
                    alt="Document scanné"
                    className="max-h-48 rounded-lg mx-auto border border-gray-200 shadow-sm"
                  />
                  <button
                    type="button"
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 shadow"
                    onClick={(e) => { e.stopPropagation(); handleFieldChange('documentPhotoUrl', ''); }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <>
                  <CameraIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                  <p className="mt-3 text-sm font-medium text-gray-900">
                    Passeport, CNI, permis de conduire ou visa
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Glissez-déposez, collez, prenez en photo ou choisissez un fichier
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    PNG, JPG, WEBP, BMP, TIFF — jusqu'à 10 Mo
                  </p>
                </>
              )}

              {/* Hidden file input */}
              <input
                id="document-upload"
                name="document-upload"
                type="file"
                accept="image/*,.pdf,.tiff,.tif,.bmp,.webp"
                className="sr-only"
                onChange={handleDocumentUpload}
                disabled={isProcessing}
              />

              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {/* Webcam / laptop camera */}
                <button
                  type="button"
                  onClick={startWebcam}
                  disabled={isProcessing}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 min-h-touch"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  {isProcessing ? 'Traitement...' : 'Caméra'}
                </button>

                {/* Mobile camera (uses native capture) */}
                <label className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 cursor-pointer min-h-touch">
                  <input type="file" accept="image/*" capture="environment" className="sr-only" onChange={handleDocumentUpload} disabled={isProcessing} />
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  Photo mobile
                </label>

                {/* File picker — any document file */}
                <label htmlFor="document-upload" className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 cursor-pointer min-h-touch">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                  Fichier
                </label>

                {/* USB scanner / flatbed — uses TWAIN/WIA capture hint */}
                <label className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 cursor-pointer min-h-touch">
                  <input type="file" accept="image/*" capture="environment" className="sr-only" onChange={handleDocumentUpload} disabled={isProcessing} />
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  Scanner
                </label>
              </div>

              <p className="mt-3 text-xs text-gray-400">
                Ctrl+V pour coller une capture d'écran
              </p>
            </div>

            {/* Webcam live view */}
            {showDocumentWebcam && (
              <div className="mt-4 bg-black rounded-lg overflow-hidden relative">
                <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-80 object-contain" />
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
                  <button
                    type="button"
                    onClick={captureWebcam}
                    className="px-6 py-3 bg-white text-gray-900 rounded-full font-medium shadow-lg hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    Capturer
                  </button>
                  <button
                    type="button"
                    onClick={stopWebcam}
                    className="px-4 py-3 bg-red-600 text-white rounded-full font-medium shadow-lg hover:bg-red-700"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Document Photo Error */}
            {errors.documentPhotoUrl && !ocrResult && (
              <p className="mt-2 text-sm text-red-600">{errors.documentPhotoUrl}</p>
            )}

            {/* OCR Result */}
            {ocrResult && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                  <span className="text-sm font-medium text-green-800">
                    Document traité avec succès (Confiance : {ocrResult.confidence}%)
                  </span>
                </div>
              </div>
            )}

            {/* OCR Error */}
            {ocrError && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
                  <span className="text-sm font-medium text-yellow-800">{ocrError}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Check-in Form - Responsive spacing */}
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Personal Information Section - Responsive padding and grid */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Identité du Client</h2>
            <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
              <div className="md:col-span-2">
                <FormInput
                  label="Nom complet"
                  name="fullName"
                  value={formData.fullName}
                  onChange={(value) => handleFieldChange('fullName', value)}
                  error={errors.fullName}
                  required
                  placeholder="Nom et prénom(s)"
                />
              </div>

              <FormSelect
                label="Genre"
                name="gender"
                value={formData.gender}
                onChange={(value) => handleFieldChange('gender', value)}
                options={[
                  { value: 'Male', label: 'Masculin' },
                  { value: 'Female', label: 'Féminin' },
                  { value: 'Other', label: 'Autre' },
                ]}
                error={errors.gender}
                required
                placeholder="Sélectionner..."
              />

              <FormInput
                label="Date de naissance"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(value) => handleFieldChange('dateOfBirth', value)}
                error={errors.dateOfBirth}
                required
              />

              {formData.dateOfBirth && isMinor(formData.dateOfBirth) && (
                <div className="md:col-span-2 p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-start sm:items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5 sm:mt-0" />
                    <span className="text-xs sm:text-sm font-medium text-yellow-800">
                      Le client est mineur. Des documents supplémentaires peuvent être requis.
                    </span>
                  </div>
                </div>
              )}

              <SearchableSelect
                label="Nationalité"
                name="nationality"
                value={formData.nationality}
                onChange={(value) => handleFieldChange('nationality', value)}
                options={ISO_NATIONALITIES.map((n) => ({ value: n, label: n }))}
                error={errors.nationality}
                required
                placeholder="Rechercher une nationalité..."
              />

              <FormInput
                label="Profession"
                name="profession"
                value={formData.profession}
                onChange={(value) => handleFieldChange('profession', value)}
                error={errors.profession}
                placeholder="Facultatif"
              />

              <FormInput
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={(value) => handleFieldChange('email', value)}
                error={errors.email}
                placeholder="Facultatif"
              />

              <FormInput
                label="Numéro de téléphone"
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={(value) => handleFieldChange('phoneNumber', value)}
                error={errors.phoneNumber}
                placeholder="Facultatif"
              />
            </div>
          </div>

          {/* Document Information Section - Responsive */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Document d'Identité</h2>
            <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
              <FormSelect
                label="Type de document"
                name="documentType"
                value={formData.documentType}
                onChange={(value) => handleFieldChange('documentType', value)}
                options={DOCUMENT_TYPES}
                error={errors.documentType}
                required
                placeholder="Sélectionner..."
              />

              <FormInput
                label="Numéro du document"
                name="documentNumber"
                value={formData.documentNumber}
                onChange={(value) => handleFieldChange('documentNumber', value)}
                error={errors.documentNumber}
                required
                placeholder="Ex : AB123456"
              />

              <FormInput
                label="Date d'expiration du document"
                name="documentExpiryDate"
                type="date"
                value={formData.documentExpiryDate}
                onChange={(value) => handleFieldChange('documentExpiryDate', value)}
                error={errors.documentExpiryDate}
                required
              />

              {/* Document Upload in Manual Mode */}
              {mode === 'manual' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scan du document d'identité
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Le scan du document d'identité est obligatoire
                  </p>
                  
                  {formData.documentPhotoUrl ? (
                    <div className="relative">
                      <img
                        src={formData.documentPhotoUrl}
                        alt="Document d'identité"
                        className="w-full max-h-48 object-contain bg-gray-50 rounded-md border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => handleFieldChange('documentPhotoUrl', '')}
                        className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                      <p className="mt-2 text-sm text-green-600 flex items-center">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Document scanné
                      </p>
                    </div>
                  ) : (
                    <div className={`border-2 border-dashed rounded-lg p-4 text-center ${
                      errors.documentPhotoUrl ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}>
                      <CameraIcon className="mx-auto h-8 w-8 text-gray-400" />
                      <label htmlFor="manual-document-upload" className="cursor-pointer">
                        <span className="mt-2 block text-sm font-medium text-gray-900">
                          Télécharger le document
                        </span>
                        <span className="mt-1 block text-xs text-gray-500">
                          PNG, JPG jusqu'à 10 Mo
                        </span>
                        <input
                          id="manual-document-upload"
                          name="manual-document-upload"
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="sr-only"
                          onChange={handleDocumentUpload}
                          disabled={isProcessing}
                        />
                        <span className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700">
                          {isProcessing ? 'Traitement...' : 'Choisir un fichier'}
                        </span>
                      </label>
                    </div>
                  )}
                  {errors.documentPhotoUrl && !formData.documentPhotoUrl && (
                    <p className="mt-2 text-sm text-red-600">{errors.documentPhotoUrl}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Accommodation Details Section - Responsive */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Détails du Séjour</h2>
            <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
              <FormSelect
                label="Type de chambre"
                name="roomType"
                value={formData.roomType}
                onChange={(value) => handleFieldChange('roomType', value)}
                options={dynamicRoomTypes}
                placeholder={dynamicRoomTypes.length === 0 ? 'Chargement des types...' : 'Sélectionner...'}
                error={errors.roomType}
                required
              />

              <FormSelect
                label="Numéro de chambre"
                name="roomNumber"
                value={formData.roomNumber}
                onChange={(value) => handleFieldChange('roomNumber', value)}
                options={availableRooms}
                error={errors.roomNumber}
                required
                placeholder={loadingRooms ? 'Chargement des chambres...' : (availableRooms.length === 0 ? 'Aucune chambre disponible' : 'Sélectionner une chambre...')}
              />

              <FormInput
                label="Date d'arrivée"
                name="checkInDate"
                type="date"
                value={formData.checkInDate}
                onChange={(value) => handleFieldChange('checkInDate', value)}
                error={errors.checkInDate}
                required
              />

              <FormInput
                label="Date de départ prévue"
                name="checkOutDate"
                type="date"
                value={formData.checkOutDate}
                onChange={(value) => handleFieldChange('checkOutDate', value)}
                error={errors.checkOutDate}
                placeholder="Facultatif"
              />

              <FormInput
                label="Provenance"
                name="provenance"
                value={formData.provenance}
                onChange={(value) => handleFieldChange('provenance', value)}
                error={errors.provenance}
                placeholder="Ville ou pays de provenance"
              />

              <FormSelect
                label="Motif du séjour"
                name="stayPurpose"
                value={formData.stayPurpose}
                onChange={(value) => handleFieldChange('stayPurpose', value)}
                options={STAY_PURPOSES}
                error={errors.stayPurpose}
                required
                placeholder="Sélectionner le motif..."
              />

              <div className="md:col-span-2">
                <FormInput
                  label="Référence de réservation"
                  name="bookingReference"
                  value={formData.bookingReference}
                  onChange={(value) => handleFieldChange('bookingReference', value)}
                  error={errors.bookingReference}
                  placeholder="Facultatif — Ex : AirBnB, Booking.com, Expedia"
                />
              </div>
            </div>
          </div>

          {/* Payment Information Section - Responsive */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Informations de Paiement</h2>
            <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
              <FormSelect
                label="Mode de paiement"
                name="paymentType"
                value={formData.paymentType}
                onChange={(value) => handleFieldChange('paymentType', value)}
                options={[
                  { value: 'USD Cash', label: 'Espèces (USD)' },
                  { value: 'Credit Card (CB)', label: 'Carte bancaire (CB)' },
                  { value: 'Bank Transfer', label: 'Virement bancaire' },
                  { value: 'Mobile Money', label: 'Mobile Money' },
                  { value: 'Cash', label: 'Espèces (CDF)' },
                  { value: 'Other', label: 'Autre' },
                ]}
                error={errors.paymentType}
                required
                placeholder="Sélectionner..."
              />

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Montant
                  <span className="text-red-500 ml-1">*</span>
                  {amountAutoPopulatedRef.current && (
                    <span className="ml-2 text-xs text-green-600 font-normal">(tarif officiel — non modifiable)</span>
                  )}
                </label>
                {amountAutoPopulatedRef.current ? (
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-100 text-gray-500 text-sm font-medium">
                      {formData.amountMode}
                    </span>
                    <div className="flex-1 min-w-0 block w-full rounded-none rounded-r-md sm:text-sm bg-gray-100 border border-gray-300 px-3 py-2 text-gray-900 font-semibold select-none">
                      {parseFloat(formData.amount || '0').toFixed(2)} {rateCurrency}
                    </div>
                  </div>
                ) : (
                <div className="mt-1 flex rounded-md shadow-sm">
                  <button
                    type="button"
                    onClick={handleAmountModeSwitch}
                    className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm font-medium hover:bg-gray-100"
                  >
                    {formData.amountMode}
                  </button>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => handleFieldChange('amount', e.target.value)}
                    className={`flex-1 min-w-0 block w-full rounded-none rounded-r-md sm:text-sm ${
                      errors.amount
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    placeholder="0.00"
                  />
                </div>
                )}
                {errors.amount && (
                  <p className="mt-2 text-sm text-red-600">{errors.amount}</p>
                )}
              </div>

              {/* Tax Breakdown Display - Responsive */}
              {taxBreakdown && (() => {
                const currency = rateCurrency || 'USD';
                return (
                <div className="md:col-span-2 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <h3 className="text-xs sm:text-sm font-medium text-blue-900 mb-2">Détail des Taxes</h3>
                  <div className="space-y-1 text-xs sm:text-sm text-blue-800">
                    <div className="flex justify-between">
                      <span>HT:</span>
                      <span className="font-medium">{formatCurrency(taxBreakdown.amountHT)} {currency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>FPT (4%):</span>
                      <span className="font-medium">{formatCurrency(taxBreakdown.amountFPT)} {currency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>TVA (16%):</span>
                      <span className="font-medium">{formatCurrency(taxBreakdown.amountTVA)} {currency}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-blue-300">
                      <span className="font-semibold">TTC:</span>
                      <span className="font-semibold">{formatCurrency(taxBreakdown.amountTTC)} {currency}</span>
                    </div>
                  </div>
                </div>
                );
              })()}
            </div>
          </div>

          {/* Tax Exemption Section */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base sm:text-lg font-medium text-gray-900">Exonération fiscale</h2>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isTaxExempt}
                  onChange={(e) => handleFieldChange('isTaxExempt', e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700">Client exonéré de taxes</span>
              </label>
            </div>

            {formData.isTaxExempt && (
              <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <FormSelect
                  label="Type d'exonération"
                  name="exemptionType"
                  value={formData.exemptionType}
                  onChange={(value) => handleFieldChange('exemptionType', value)}
                  options={[
                    { value: 'diplomatic', label: 'Diplomatique' },
                    { value: 'ngo', label: 'ONG / Organisation internationale' },
                    { value: 'government', label: 'Gouvernemental' },
                    { value: 'military', label: 'Militaire / Force de maintien de la paix' },
                    { value: 'other', label: 'Autre' },
                  ]}
                  required
                  placeholder="Sélectionner le type..."
                />

                <FormInput
                  label="N° du document d'exonération"
                  name="exemptionReference"
                  value={formData.exemptionReference}
                  onChange={(value) => handleFieldChange('exemptionReference', value)}
                  placeholder="Ex : DIP-2026-001234"
                  required
                />

                <FormInput
                  label="Date de début de validité"
                  name="exemptionValidFrom"
                  type="date"
                  value={formData.exemptionValidFrom}
                  onChange={(value) => handleFieldChange('exemptionValidFrom', value)}
                />

                <FormInput
                  label="Date de fin de validité"
                  name="exemptionValidUntil"
                  type="date"
                  value={formData.exemptionValidUntil}
                  onChange={(value) => handleFieldChange('exemptionValidUntil', value)}
                />

                <div className="md:col-span-2">
                  <FormInput
                    label="Motif de l'exonération"
                    name="exemptionReason"
                    value={formData.exemptionReason}
                    onChange={(value) => handleFieldChange('exemptionReason', value)}
                    placeholder="Ex : Accord bilatéral, Convention de Vienne, Mission officielle..."
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Certificat d'exonération (photo ou scan)
                  </label>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = () => {
                          const base64 = (reader.result as string).split(',')[1] || '';
                          handleFieldChange('exemptionCertificate', base64);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  {formData.exemptionCertificate && (
                    <p className="mt-1 text-xs text-green-600">Certificat chargé</p>
                  )}
                </div>

                <div className="md:col-span-2 bg-yellow-100 border border-yellow-300 rounded p-3">
                  <p className="text-sm text-yellow-800 font-medium">
                    Ce client est exonéré de la TVA (16%). La taxe FPT (4%) reste applicable et sera incluse dans le calcul.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Photos Section - Responsive */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Photos & Signature</h2>
            <p className="text-xs sm:text-sm text-gray-500 mb-4">
              Optionnel — Si complétés, ces éléments seront inclus dans la fiche de police
            </p>
            <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Photo du visage
                  <span className="text-gray-400 ml-1 text-xs">(facultatif)</span>
                </label>
                {formData.facePhotoBase64 ? (
                  <div className="relative">
                    <img
                      src={formData.facePhotoBase64}
                      alt="Face"
                      className="w-full h-48 object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => handleFieldChange('facePhotoBase64', '')}
                      className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                    <button
                      type="button"
                      onClick={() => setShowFaceWebcam(true)}
                      className="w-full h-48 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center hover:border-gray-400"
                    >
                      <CameraIcon className="h-8 w-8 text-gray-400" />
                    <span className="mt-2 text-sm text-gray-500">Prendre la photo</span>
                  </button>
                )}
                {errors.facePhotoBase64 && (
                  <p className="mt-2 text-sm text-red-600">{errors.facePhotoBase64}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Signature
                  <span className="text-gray-400 ml-1 text-xs">(facultatif)</span>
                </label>
                {formData.signatureBase64 ? (
                  <div className="relative">
                    <img
                      src={formData.signatureBase64}
                      alt="Signature"
                      className="w-full h-48 object-contain bg-gray-50 rounded-md border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => handleFieldChange('signatureBase64', '')}
                      className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowSignaturePad(true)}
                    className="w-full h-48 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center hover:border-gray-400"
                  >
                    <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                    <span className="mt-2 text-sm text-gray-500">Ajouter la signature</span>
                  </button>
                )}
                {errors.signatureBase64 && (
                  <p className="mt-2 text-sm text-red-600">{errors.signatureBase64}</p>
                )}
              </div>
            </div>
          </div>

          {/* Webcam Modal */}
          <WebcamCapture
            isOpen={showFaceWebcam}
            onClose={() => setShowFaceWebcam(false)}
            onCapture={handleWebcamCapture}
          />

          {/* Signature Pad Modal */}
          <SignaturePad
            isOpen={showSignaturePad}
            onClose={() => setShowSignaturePad(false)}
            onSave={handleSignatureCapture}
          />

          {/* Success Message */}
          {successMessage && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-sm font-medium text-green-800">{successMessage}</span>
              </div>
            </div>
          )}

          {/* Submit Error */}
          {errors.submit && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                <span className="text-sm font-medium text-red-800">{errors.submit}</span>
              </div>
            </div>
          )}
          
          {/* Submit Button - Responsive, stack on mobile */}
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
            <button
              type="button"
              onClick={() => navigate('/guests')}
              className="w-full sm:w-auto px-4 py-2.5 sm:py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-h-touch"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto px-4 py-2.5 sm:py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-300 disabled:opacity-50 disabled:cursor-not-allowed min-h-touch"
            >
              {isSubmitting ? 'Enregistrement en cours...' : 'Enregistrer la Fiche de Police'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckInPage;
